/*
  assets/customer-result-history-rh-e1.js — FAMILY-RECORD-LIBRARY-V9 + PRODUCT-RESULT-LANE-V2
  Owner: existing /pages/result-history record rendering.
  Local data contracts: window.LittleGFamilyData.listRecords() and window.LittleGResultStorage.listAll().
  Server product-result read contract: signed Shopify App Proxy -> gcity-core-api /api/delivery/jobs/history.
  Route inputs: canonical Upload / Explore URLs are supplied by sections/customer-result-history.liquid.
  Product availability truth: server artifact_status + download_allowed; Delivery Job status is never re-invented client-side.
  Locale contract: window.LITTLEG_LOCALE with section data-locale fallback.
  Boundary: no direct storage-key access, persistence mutation, GFun mutation, checkout mutation,
  public wall, ranking or fabricated completion state.
*/
(function resultRecordLibrary(window, document) {
  'use strict';

  var VERSION = 'FAMILY-RECORD-LIBRARY-V9+PRODUCT-RESULT-LANE-V2';
  var ROOT_SELECTOR = '[data-customer-result-history]';
  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();
  var stateByRoot = new WeakMap();

  function all(root, selector) {
    var scope = root || document;
    return scope.querySelectorAll
      ? Array.prototype.slice.call(scope.querySelectorAll(selector))
      : [];
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function first() {
    for (var index = 0; index < arguments.length; index += 1) {
      var value = arguments[index];
      if (value !== undefined && value !== null && clean(value)) return clean(value);
    }
    return '';
  }

  function asArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value !== 'object') return [];
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.cards)) return value.cards;
    if (Array.isArray(value.history)) return value.history;
    if (Array.isArray(value.results)) return value.results;
    return [value];
  }

  function productRecordsFromPayload(payload) {
    var items = Array.isArray(payload)
      ? payload
      : payload && Array.isArray(payload.items)
        ? payload.items
        : null;

    if (!items) return null;

    return items.filter(function (item) {
      return item && typeof item === 'object' && clean(item.job_id || item.id);
    });
  }

  function truthy(value) {
    var normalized = clean(value).toLowerCase();
    return (
      value === true ||
      value === 1 ||
      value === '1' ||
      normalized === 'true' ||
      normalized === 'yes'
    );
  }

  function sameOriginUrl(value) {
    var raw = clean(value);
    if (!raw) return null;

    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url;
    } catch (_error) {
      return null;
    }
  }

  function httpUrl(value) {
    var raw = clean(value);
    if (!raw) return null;

    try {
      var url = new URL(raw, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
    } catch (_error) {
      return null;
    }
  }

  function requestedJobId() {
    try {
      var url = new URL(window.location.href);
      var jobId = clean(url.searchParams.get('job_id'));
      var legacyId = clean(url.searchParams.get('checkout_pending_id'));

      if (!jobId && legacyId) {
        url.searchParams.set('job_id', legacyId);
        url.searchParams.delete('checkout_pending_id');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        return legacyId;
      }

      return jobId || legacyId;
    } catch (_error) {
      return '';
    }
  }

  function prioritizeRequestedJob(records) {
    var requested = requestedJobId();
    var list = Array.isArray(records) ? records.slice() : [];
    if (!requested) return list;

    return list.sort(function (left, right) {
      var leftMatch = recordId(left, -1) === requested ? 1 : 0;
      var rightMatch = recordId(right, -1) === requested ? 1 : 0;
      return rightMatch - leftMatch;
    });
  }

  function localeOwner() {
    var owner = window.LITTLEG_LOCALE;
    return owner && typeof owner.getCode === 'function' ? owner : null;
  }

  function localeCode(root) {
    var owner = localeOwner();
    if (owner) return clean(owner.getCode()) || 'zh-HK';

    var raw = clean(
      (root && root.getAttribute('data-locale')) ||
        document.documentElement.getAttribute('lang') ||
        (window.Shopify && window.Shopify.locale) ||
        'zh-HK'
    ).replace(/_/g, '-');
    var lower = raw.toLowerCase();

    if (
      lower.indexOf('zh-cn') === 0 ||
      lower.indexOf('zh-sg') === 0 ||
      lower.indexOf('hans') !== -1
    )
      return 'zh-CN';
    if (lower.indexOf('zh-tw') === 0) return 'zh-TW';
    if (lower.indexOf('zh') === 0 || lower.indexOf('hant') !== -1) return 'zh-HK';
    if (lower.indexOf('en') === 0) return 'en';
    return 'zh-HK';
  }

  function copy(root, traditional, simplified, english, taiwan) {
    var code = localeCode(root);
    if (code === 'en') return english;
    if (code === 'zh-CN') return simplified;
    if (code === 'zh-TW') return taiwan || traditional;
    return traditional;
  }

  function routeWithParams(root, attribute, params) {
    var url = sameOriginUrl(root && root.getAttribute(attribute));
    if (!url) return '';

    Object.keys(params || {}).forEach(function (key) {
      var value = params[key];
      if (value === undefined || value === null || value === '') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, String(value));
      }
    });

    return url.pathname + url.search + url.hash;
  }

  function safeProductHandle(value) {
    var handle = clean(value).toLowerCase();
    return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? handle : '';
  }

  function recordId(record, index) {
    return first(
      record && record.record_id,
      record && record.result_id,
      record && record.card_id,
      record && record.job_id,
      record && record.id,
      'record-' + index
    );
  }

  function savedAt(record) {
    return first(
      record && record.updated_at,
      record && record.confirmed_at,
      record && record.saved_at,
      record && record.created_at,
      record && record.generated_at
    );
  }

  function dateOnly(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean(value));
    if (!match) return null;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var date = new Date(year, month - 1, day, 12, 0, 0, 0);

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
      return null;

    return date;
  }

  function dateLabel(root, value) {
    var raw = clean(value);
    if (!raw) return copy(root, '時間未提供', '时间未提供', 'Time unavailable');

    var date = dateOnly(raw) || new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;

    var owner = localeOwner();
    if (owner && typeof owner.formatDate === 'function') return owner.formatDate(date);

    try {
      return new Intl.DateTimeFormat(localeCode(root), {
        timeZone: 'Asia/Hong_Kong',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch (_error) {
      return raw;
    }
  }

  function create(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = String(content);
    return node;
  }

  function actionLink(label, href, dark) {
    if (!href) return null;
    var link = create(
      'a',
      'customer-result-history__btn' + (dark ? ' customer-result-history__btn--dark' : ''),
      label
    );
    link.href = href;
    link.setAttribute('data-no-legacy-normalize', 'true');
    return link;
  }

  function actionButton(label, jobId) {
    if (!jobId) return null;
    var button = create(
      'button',
      'customer-result-history__btn customer-result-history__btn--dark',
      label
    );
    button.type = 'button';
    button.setAttribute('data-product-result-open', '');
    button.setAttribute('data-product-job-id', jobId);
    return button;
  }

  function familyTitle(root, record) {
    var notice = record && record.notice && typeof record.notice === 'object' ? record.notice : {};
    var fields = Array.isArray(notice.fields) ? notice.fields : [];
    var activity = '';

    fields.some(function (field) {
      if (!field || clean(field.guardian_state).toLowerCase() === 'removed') return false;
      var key = clean(field.key || field.name).toLowerCase();
      if (key !== 'activity_name') return false;
      activity = clean(field.value || field.text);
      return Boolean(activity);
    });

    return first(
      activity,
      notice.activity_name,
      record && record.activity_name,
      copy(root, '通告準備記錄', '通告准备记录', 'Notice preparation record')
    );
  }

  function familySummary(root, record) {
    var packing =
      record && record.packing_list && typeof record.packing_list === 'object'
        ? record.packing_list
        : {};
    var checklist = asArray(record && record.checklist);
    var groups = ['required_items', 'suggested_items', 'custom_items'];
    var count = groups.reduce(function (total, key) {
      return total + asArray(packing[key]).length;
    }, 0);
    if (!count) count = checklist.length;

    return [
      count
        ? copy(root, count + ' 項物品', count + ' 项物品', count + ' items')
        : copy(root, '未提供物品數量', '未提供物品数量', 'Item count unavailable'),
      copy(root, '私人本機記錄', '私人本机记录', 'Private local record'),
      dateLabel(root, savedAt(record)),
    ].join(' · ');
  }

  function cardTitle(root, record) {
    return first(
      record && record.mission_name,
      record && record.title,
      record && record.hero,
      record && record.location,
      copy(root, '城市探索卡', '城市探索卡', 'City exploration card')
    );
  }

  function cardSummary(root, record) {
    var clues = asArray(record && (record.clues || record.clue_labels));
    var description = first(
      record && record.result_line,
      record && record.summary,
      record && record.mission_description
    );

    return [
      description ||
        (clues.length
          ? clues.slice(0, 3).map(clean).filter(Boolean).join(' · ')
          : copy(root, '本機城市觀察', '本机城市观察', 'Local city observation')),
      dateLabel(root, savedAt(record)),
    ].join(' · ');
  }

  function humanizeHandle(value) {
    var text = clean(value).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
    return text.replace(/\b[a-z]/g, function (letter) {
      return letter.toUpperCase();
    });
  }

  function productTitle(root, record) {
    var handle = safeProductHandle(record && record.product_handle);
    if (handle === 'glg-spatial-valuation-preview') {
      return copy(root, '買樓前物業快檢', '买楼前物业快检', 'Home Buying Property Check');
    }
    return humanizeHandle(handle) || copy(root, '產品結果', '产品结果', 'Product result');
  }

  function productResultAvailable(record) {
    return Boolean(
      record &&
      clean(record.artifact_status).toLowerCase() === 'created' &&
      truthy(record.download_allowed)
    );
  }

  function productStatusLabel(root, record) {
    if (productResultAvailable(record)) {
      return copy(root, '結果可用', '结果可用', 'Result available');
    }

    var status = clean(record && record.status).toLowerCase();
    if (status === 'created') {
      return copy(root, '已建立', '已创建', 'Created');
    }
    if (status === 'pending_review') {
      return copy(root, '等待專業覆核', '等待专业复核', 'Professional review pending');
    }
    if (status === 'not_ready') {
      return copy(root, '尚未生成', '尚未生成', 'Not ready yet');
    }
    if (status === 'blocked') {
      return copy(root, '需要下一步', '需要下一步', 'Action required');
    }
    if (status === 'failed') {
      return copy(root, '未完成', '未完成', 'Not completed');
    }
    return copy(root, '狀態更新中', '状态更新中', 'Status updating');
  }

  function productSummary(root, record) {
    return [productStatusLabel(root, record), dateLabel(root, savedAt(record))].join(' · ');
  }

  function familyReuseHref(root, id) {
    return routeWithParams(root, 'data-upload-url', {
      view: 'littleg-upload',
      source: 'result-history',
      intent: 'reuse',
      record_id: id,
    });
  }

  function cityReplayHref(root, id) {
    return routeWithParams(root, 'data-explore-url', {
      view: 'game',
      screen: 'lobby',
      source: 'result-history',
      intent: 'replay',
      record_id: id,
    });
  }

  function renderRecordCard(root, record, index, type) {
    var id = recordId(record, index);
    var article = create('article', 'customer-result-history__card');
    article.setAttribute('data-record-id', id);
    article.setAttribute('data-record-type', type);

    article.appendChild(
      create(
        'span',
        'customer-result-history__badge',
        type === 'family'
          ? copy(root, '家庭記錄', '家庭记录', 'Family record')
          : copy(root, '城市探索', '城市探索', 'City exploration')
      )
    );
    article.appendChild(
      create('h2', '', type === 'family' ? familyTitle(root, record) : cardTitle(root, record))
    );
    article.appendChild(
      create(
        'p',
        'customer-result-history__card-observation',
        type === 'family' ? familySummary(root, record) : cardSummary(root, record)
      )
    );

    var href = type === 'family' ? familyReuseHref(root, id) : cityReplayHref(root, id);
    var action = actionLink(
      type === 'family'
        ? copy(root, '再次使用', '再次使用', 'Use again')
        : copy(root, '再玩一次', '再玩一次', 'Play again'),
      href,
      true
    );

    if (action) {
      var actions = create('div', 'customer-result-history__card-actions');
      actions.appendChild(action);
      article.appendChild(actions);
    }

    return article;
  }

  function renderPlaybookRecordCard(root, record, index) {
    var id = recordId(record, index);
    var article = create('article', 'customer-result-history__card');
    article.setAttribute('data-record-id', id);
    article.setAttribute('data-record-type', 'playbook');

    article.appendChild(
      create(
        'span',
        'customer-result-history__badge',
        copy(root, '本機項目草稿', '本机项目草稿', 'Local project draft')
      )
    );
    article.appendChild(
      create(
        'h2',
        '',
        first(record.generator_name, copy(root, '項目劇本模板', '项目剧本模板', 'Project playbook'))
      )
    );
    article.appendChild(
      create(
        'p',
        'customer-result-history__card-observation',
        [first(record.playbook_stage, record.playbook_output), dateLabel(root, savedAt(record))]
          .filter(Boolean)
          .join(' · ')
      )
    );

    var details = create('details', 'customer-result-history__card-details');
    details.appendChild(
      create(
        'summary',
        'customer-result-history__btn',
        copy(root, '查看草稿內容', '查看草稿内容', 'View draft details')
      )
    );
    details.appendChild(
      create(
        'p',
        'customer-result-history__card-observation',
        copy(root, '項目問題：', '项目问题：', 'Project question: ') + clean(record.query)
      )
    );
    details.appendChild(
      create(
        'p',
        'customer-result-history__card-observation',
        copy(root, '預計成果：', '预计成果：', 'Expected output: ') + clean(record.playbook_output)
      )
    );
    details.appendChild(
      create(
        'p',
        'customer-result-history__card-observation',
        copy(
          root,
          '只保存在此瀏覽器，未提交為正式項目。',
          '仅保存在此浏览器，未提交为正式项目。',
          'Saved only in this browser; not a formal project submission.'
        )
      )
    );
    article.appendChild(details);

    var handle = safeProductHandle(record.generator_handle);
    if (handle) {
      var action = actionLink(
        copy(root, '繼續編輯草稿', '继续编辑草稿', 'Continue editing draft'),
        '/pages/generator?template=' +
          encodeURIComponent(handle) +
          '&source=result-history&intent=resume-playbook&record_id=' +
          encodeURIComponent(id),
        false
      );
      if (action) {
        var actions = create('div', 'customer-result-history__card-actions');
        actions.appendChild(action);
        article.appendChild(actions);
      }
    }
    return article;
  }

  function renderProductResultCard(root, record, index) {
    var id = recordId(record, index);
    var handle = safeProductHandle(record && record.product_handle);
    var status = clean(record && record.status).toLowerCase();
    var available = productResultAvailable(record);
    var article = create(
      'article',
      'customer-result-history__card customer-result-history__card--product'
    );
    article.setAttribute('data-record-id', id);
    article.setAttribute('data-record-type', 'product');
    article.setAttribute('data-product-handle', handle);
    article.setAttribute('data-product-job-status', status);
    article.setAttribute('data-product-result-ready', available ? 'true' : 'false');

    article.appendChild(
      create(
        'span',
        'customer-result-history__badge',
        copy(root, '產品結果', '产品结果', 'Product result')
      )
    );
    article.appendChild(create('h2', '', productTitle(root, record)));
    article.appendChild(
      create('p', 'customer-result-history__card-observation', productSummary(root, record))
    );

    if (available) {
      var action = actionButton(copy(root, '查看結果', '查看结果', 'View result'), id);
      if (action) {
        var actions = create('div', 'customer-result-history__card-actions');
        actions.appendChild(action);
        article.appendChild(actions);
      }
    }

    return article;
  }

  function readFamilyRecords() {
    var owner = window.LittleGFamilyData;
    if (!owner || typeof owner.listRecords !== 'function') {
      return { available: false, records: [] };
    }

    try {
      var value = owner.listRecords();
      if (value && typeof value.then === 'function') return { available: false, records: [] };
      return { available: true, records: asArray(value) };
    } catch (_error) {
      return { available: false, records: [] };
    }
  }

  function readCityCards() {
    var owner = window.LittleGResultStorage;
    var family = window.LittleGFamilyData;
    if (!owner || typeof owner.listAll !== 'function' || typeof owner.isCityCard !== 'function') {
      return { available: false, records: [] };
    }
    if (!family || typeof family.isFamilyRecord !== 'function') {
      return { available: false, records: [] };
    }

    try {
      var value = owner.listAll();
      if (value && typeof value.then === 'function') return { available: false, records: [] };
      return {
        available: true,
        records: asArray(value).filter(function (entry) {
          return entry && !family.isFamilyRecord(entry) && owner.isCityCard(entry);
        }),
      };
    } catch (_error) {
      return { available: false, records: [] };
    }
  }

  function readPlaybookRecords() {
    var owner = window.LittleGResultStorage;
    if (!owner || typeof owner.listAll !== 'function') return { available: false, records: [] };
    try {
      var value = owner.listAll();
      if (value && typeof value.then === 'function') return { available: false, records: [] };
      return {
        available: true,
        records: asArray(value).filter(function (entry) {
          return (
            entry &&
            clean(entry.storage_path) === 'generator' &&
            clean(entry.lead_type) === 'professional_project_playbook' &&
            clean(entry.source) === 'gcitypartners-project-playbook'
          );
        }),
      };
    } catch (_error) {
      return { available: false, records: [] };
    }
  }

  function requestedCityRecordId() {
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('intent') !== 'saved-record') return '';
      return clean(url.searchParams.get('record_id')).slice(0, 120);
    } catch (_error) {
      return '';
    }
  }

  function focusRequestedCityCard(grid, state) {
    var requestedId = requestedCityRecordId();
    if (!requestedId || !grid || state.focusedCityRecordId === requestedId) return;
    var target = all(grid, '[data-record-type="city"][data-record-id]').find(function (card) {
      return card.getAttribute('data-record-id') === requestedId;
    });
    if (!target) return;

    target.setAttribute('tabindex', '-1');
    target.setAttribute('data-requested-city-record', 'true');
    state.focusedCityRecordId = requestedId;
    try {
      target.focus({ preventScroll: true });
    } catch (_error) {
      target.focus();
    }
    try {
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
    } catch (_error) {}
  }

  function focusRequestedPlaybookRecord(grid, state) {
    var requestedId = requestedCityRecordId();
    if (!requestedId || !grid || state.focusedPlaybookRecordId === requestedId) return;
    var target = all(grid, '[data-record-type="playbook"][data-record-id]').find(function (card) {
      return card.getAttribute('data-record-id') === requestedId;
    });
    if (!target) return;

    target.setAttribute('tabindex', '-1');
    target.setAttribute('data-requested-playbook-record', 'true');
    state.focusedPlaybookRecordId = requestedId;
    try {
      target.focus({ preventScroll: true });
    } catch (_error) {
      target.focus();
    }
    try {
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
    } catch (_error) {}
  }

  function serverProductResultsEnabled(root) {
    return clean(root && root.getAttribute('data-server-product-results')).toLowerCase() === 'true';
  }

  function productResultsUrl(root) {
    var url = sameOriginUrl(root && root.getAttribute('data-product-results-url'));
    return url ? url.pathname + url.search + url.hash : '';
  }

  function productDownloadUrl(root, jobId) {
    var url = sameOriginUrl(root && root.getAttribute('data-product-results-url'));
    if (!url || !jobId || !/\/history$/.test(url.pathname)) return '';

    url.pathname = url.pathname.replace(/\/history$/, '/download');
    url.search = '';
    url.hash = '';
    url.searchParams.set('job_id', jobId);
    return url.pathname + url.search;
  }

  function loadProductResults(root, force) {
    var state = stateByRoot.get(root);
    if (!state) return;

    if (!serverProductResultsEnabled(root)) {
      state.productRecords = [];
      state.productLoaded = true;
      state.productLoading = false;
      state.productError = '';
      schedule(root);
      return;
    }

    var url = productResultsUrl(root);
    if (!url) {
      state.productRecords = [];
      state.productLoaded = true;
      state.productLoading = false;
      state.productError = 'product_results_url_missing';
      schedule(root);
      return;
    }

    if (state.productLoading || (state.productLoaded && !force)) return;

    if (state.productController) state.productController.abort();
    state.productController = typeof AbortController === 'function' ? new AbortController() : null;
    state.productLoading = true;
    state.productError = '';
    schedule(root);

    window
      .fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: state.productController ? state.productController.signal : undefined,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (payload) {
            if (!response.ok || (payload && payload.ok === false)) {
              var error = new Error('product_results_unavailable');
              error.status = response.status;
              throw error;
            }
            return payload;
          });
      })
      .then(function (payload) {
        if (!stateByRoot.has(root)) return;

        var records = productRecordsFromPayload(payload);
        if (!records) throw new Error('product_results_contract_invalid');

        state.productRecords = prioritizeRequestedJob(records);
        state.productLoaded = true;
        state.productError = '';
      })
      .catch(function (error) {
        if (!stateByRoot.has(root)) return;
        if (error && error.name === 'AbortError') return;
        state.productRecords = [];
        state.productLoaded = true;
        state.productError = clean(error && error.message) || 'product_results_unavailable';
      })
      .finally(function () {
        if (!stateByRoot.has(root)) return;
        state.productLoading = false;
        state.productController = null;
        schedule(root);
      });
  }

  function openProductResult(root, button) {
    var state = stateByRoot.get(root);
    var jobId = clean(button && button.getAttribute('data-product-job-id'));
    var url = productDownloadUrl(root, jobId);
    var status = root && root.querySelector('[data-product-result-status]');

    if (!state || !button || !jobId || !url) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    window
      .fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (payload) {
            if (!response.ok || (payload && payload.ok === false)) {
              throw new Error('product_result_access_unavailable');
            }
            return payload;
          });
      })
      .then(function (payload) {
        if (!stateByRoot.has(root)) return;

        var target = httpUrl(payload && payload.download_url);
        if (!truthy(payload && payload.download_eligible) || !target) {
          if (status) {
            status.hidden = false;
            status.textContent = copy(
              root,
              '這個結果目前未開放存取。',
              '这个结果目前未开放访问。',
              'This result is not available for access yet.'
            );
          }
          return;
        }

        window.location.assign(target.href);
      })
      .catch(function () {
        if (!stateByRoot.has(root)) return;
        if (status) {
          status.hidden = false;
          status.textContent = copy(
            root,
            '暫時無法開啟這個產品結果。',
            '暂时无法打开这个产品结果。',
            'This product result is temporarily unavailable.'
          );
        }
      })
      .finally(function () {
        if (!button.isConnected) return;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });
  }

  function setCount(root, selector, value) {
    all(root, selector).forEach(function (node) {
      node.textContent = String(value);
    });
  }

  function render(root) {
    if (!root || !root.isConnected) return;
    root.setAttribute('data-locale', localeCode(root));

    var state = stateByRoot.get(root) || {};
    var familyResult = readFamilyRecords();
    var cityResult = readCityCards();
    var playbookResult = readPlaybookRecords();
    var localOwnersReady =
      familyResult.available && cityResult.available && playbookResult.available;
    var status = root.querySelector('[data-status]');
    var productStatus = root.querySelector('[data-product-result-status]');
    var productLane = root.querySelector('[data-server-connected="true"]');

    var familyRecords = familyResult.available ? familyResult.records : [];
    var cityCards = cityResult.available ? cityResult.records : [];
    var playbookRecords = playbookResult.available ? playbookResult.records : [];
    var productRecords = Array.isArray(state.productRecords) ? state.productRecords : [];
    var familyGrid = root.querySelector('[data-family-record-grid]');
    var cardGrid = root.querySelector('[data-card-grid]');
    var playbookGrid = root.querySelector('[data-playbook-record-grid]');
    var playbookLane = root.querySelector('[data-playbook-record-lane]');
    var productGrid = root.querySelector('[data-product-result-grid]');
    var empty = root.querySelector('[data-card-empty]');
    var stats = root.querySelector('.customer-result-history__stats');
    var lanes = root.querySelector('.customer-result-history__record-lanes');

    if (familyGrid && familyResult.available) {
      familyGrid.replaceChildren();
      familyRecords.forEach(function (record, index) {
        familyGrid.appendChild(renderRecordCard(root, record, index, 'family'));
      });
    }

    if (cardGrid && cityResult.available) {
      cardGrid.replaceChildren();
      cityCards.forEach(function (record, index) {
        cardGrid.appendChild(renderRecordCard(root, record, index, 'city'));
      });
    }

    if (playbookGrid && playbookResult.available) {
      playbookGrid.replaceChildren();
      playbookRecords.forEach(function (record, index) {
        playbookGrid.appendChild(renderPlaybookRecordCard(root, record, index));
      });
    }
    if (playbookLane) playbookLane.hidden = !playbookRecords.length;

    if (productGrid) {
      productGrid.replaceChildren();
      productRecords.forEach(function (record, index) {
        productGrid.appendChild(renderProductResultCard(root, record, index));
      });
    }

    setCount(root, '[data-stat-family]', familyRecords.length);
    setCount(root, '[data-stat-cards]', cityCards.length);
    setCount(root, '[data-stat-products]', productRecords.length);

    if (productStatus) {
      productStatus.hidden = false;
      if (state.productLoading && !state.productLoaded) {
        productStatus.textContent = copy(
          root,
          '正在載入帳戶產品結果…',
          '正在加载账号产品结果…',
          'Loading account product results…'
        );
      } else if (state.productError) {
        productStatus.textContent = copy(
          root,
          '產品結果暫時無法載入；本機家庭與城市記錄不受影響。',
          '产品结果暂时无法加载；本机家庭与城市记录不受影响。',
          'Product results are temporarily unavailable. Local family and city records are unaffected.'
        );
      } else if (state.productLoaded && productRecords.length === 0) {
        productStatus.textContent = copy(
          root,
          '目前沒有已連接的產品結果。',
          '目前没有已连接的产品结果。',
          'No connected product results yet.'
        );
      } else if (state.productLoaded) {
        productStatus.textContent = copy(
          root,
          '已載入帳戶產品結果。',
          '已加载账号产品结果。',
          'Account product results loaded.'
        );
      }
    }

    var hasRecords =
      familyRecords.length + cityCards.length + playbookRecords.length + productRecords.length > 0;
    var loadingAnything = !localOwnersReady || state.productLoading;
    if (empty) empty.hidden = hasRecords || loadingAnything;
    if (stats) stats.hidden = !(localOwnersReady || state.productLoaded || state.productLoading);
    if (lanes) lanes.hidden = !(hasRecords || loadingAnything);

    root.setAttribute('aria-busy', loadingAnything ? 'true' : 'false');
    if (productLane) productLane.setAttribute('aria-busy', state.productLoading ? 'true' : 'false');

    if (status) {
      status.hidden = false;
      if (!localOwnersReady) {
        status.textContent = copy(
          root,
          '正在載入這部裝置的私人記錄…',
          '正在加载此设备的私人记录…',
          'Loading private records from this device…'
        );
      } else if (hasRecords) {
        status.textContent = copy(root, '記錄已更新。', '记录已更新。', 'Records are up to date.');
      } else if (!state.productLoading) {
        status.textContent = '';
        status.hidden = true;
      }
    }

    root.setAttribute('data-family-record-count', String(familyRecords.length));
    root.setAttribute('data-city-card-count', String(cityCards.length));
    root.setAttribute('data-playbook-record-count', String(playbookRecords.length));
    root.setAttribute('data-product-result-count', String(productRecords.length));
    root.setAttribute('data-empty-state', hasRecords ? 'false' : 'true');
    root.setAttribute(
      'data-record-storage-state',
      localOwnersReady ? 'ready' : 'owner-unavailable'
    );
    root.setAttribute(
      'data-product-result-state',
      state.productLoading
        ? 'loading'
        : state.productError
          ? 'unavailable'
          : state.productLoaded
            ? 'ready'
            : 'idle'
    );
    root.setAttribute(
      'data-runtime-ready',
      localOwnersReady && state.productLoaded ? 'true' : 'partial'
    );
    focusRequestedCityCard(cardGrid, state);
    focusRequestedPlaybookRecord(playbookGrid, state);
  }

  function schedule(root) {
    var state = stateByRoot.get(root);
    if (!state || state.raf != null) return;

    state.raf = window.requestAnimationFrame(function () {
      state.raf = null;
      render(root);
    });
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;
    stateByRoot.set(root, {
      raf: null,
      focusedCityRecordId: '',
      focusedPlaybookRecordId: '',
      productRecords: [],
      productLoaded: false,
      productLoading: false,
      productError: '',
      productController: null,
    });
    mountedRoots.add(root);
    root.setAttribute('data-runtime-bound', VERSION);
    schedule(root);
    loadProductResults(root, false);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;
    if (state.raf != null) window.cancelAnimationFrame(state.raf);
    if (state.productController) state.productController.abort();
    mountedRoots.delete(root);
    stateByRoot.delete(root);
    root.removeAttribute('data-runtime-bound');
    root.removeAttribute('data-runtime-ready');
    root.removeAttribute('data-record-storage-state');
    root.removeAttribute('data-product-result-state');
    root.removeAttribute('aria-busy');
    var productLane = root.querySelector('[data-server-connected="true"]');
    if (productLane) productLane.removeAttribute('aria-busy');
  }

  function mountAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    all(target, ROOT_SELECTOR).forEach(mount);
  }

  function destroyAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) destroy(target);
    all(target, ROOT_SELECTOR).forEach(destroy);
  }

  function refreshLocal() {
    mountedRoots.forEach(schedule);
  }

  function refreshProductResults() {
    mountedRoots.forEach(function (root) {
      loadProductResults(root, true);
    });
  }

  document.addEventListener('littleg:family-record-saved', refreshLocal);
  document.addEventListener('littleg:family-data-ready', refreshLocal);
  document.addEventListener('littleg:locale-ready', refreshLocal);

  [
    'littleg:city-mission:result-saved',
    'littleg:city-mission:result-updated',
    'littleg:city-mission:result-removed',
    'littleg:city-mission:results-cleared',
  ].forEach(function (name) {
    window.addEventListener(name, refreshLocal);
    document.addEventListener(name, refreshLocal);
  });

  document.addEventListener('click', function (event) {
    var importButton =
      event.target && event.target.closest
        ? event.target.closest('[data-playbook-import-open]')
        : null;
    if (importButton) {
      var importRoot = importButton.closest(ROOT_SELECTOR);
      var fileInput = importRoot && importRoot.querySelector('[data-playbook-import-file]');
      if (fileInput && stateByRoot.has(importRoot)) {
        event.preventDefault();
        fileInput.click();
      }
      return;
    }
    var button =
      event.target && event.target.closest
        ? event.target.closest('[data-product-result-open]')
        : null;
    if (!button) return;

    var root = button.closest(ROOT_SELECTOR);
    if (!root || !stateByRoot.has(root)) return;

    event.preventDefault();
    openProductResult(root, button);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-playbook-import-file]')) return;
    var root = input.closest(ROOT_SELECTOR);
    var file = input.files && input.files[0];
    var status = root && root.querySelector('[data-playbook-import-status]');
    var button = root && root.querySelector('[data-playbook-import-open]');
    if (!root || !stateByRoot.has(root) || !file || !status || !button) return;
    function feedback(message) {
      status.hidden = false;
      status.textContent = message;
    }
    var failure = copy(
      root,
      '匯入失敗，請選擇有效的項目草稿 JSON。',
      '导入失败，请选择有效的项目草稿 JSON。',
      'Import failed. Choose a valid Project Playbook JSON file.'
    );
    if (file.size > 262144 || !/\.json$/i.test(file.name)) {
      feedback(failure);
      input.value = '';
      return;
    }
    button.disabled = true;
    feedback(copy(root, '正在匯入此瀏覽器…', '正在导入此浏览器…', 'Importing into this browser…'));
    Promise.resolve()
      .then(function () {
        return file.text();
      })
      .then(function (source) {
        if (source.length > 262144) throw new Error('file_too_large');
        var owner = window.LittleGResultStorage;
        var stored =
          owner && typeof owner.importPlaybookJson === 'function'
            ? owner.importPlaybookJson(JSON.parse(source))
            : null;
        if (!stored || !stored.record_id) throw new Error('local_import_failed');
        var url = new URL(window.location.href);
        url.searchParams.set('source', 'project-playbook');
        url.searchParams.set('intent', 'saved-record');
        url.searchParams.set('record_id', stored.record_id);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        feedback(
          copy(
            root,
            '草稿已匯入此瀏覽器。可在下方繼續編輯。',
            '草稿已导入此浏览器，可在下方继续编辑。',
            'Draft imported into this browser. Continue editing below.'
          )
        );
        refreshLocal();
      })
      .catch(function () {
        feedback(failure);
      })
      .then(function () {
        button.disabled = false;
        input.value = '';
      });
  });

  document.addEventListener('gcity:product-result-updated', refreshProductResults);
  window.addEventListener('storage', refreshLocal);
  window.addEventListener('pageshow', function () {
    refreshLocal();
    refreshProductResults();
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event && event.target ? event.target : document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event && event.target ? event.target : document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  } else {
    mountAll(document);
  }
})(window, document);
